// First-pass SNAP/EBT eligibility heuristic, mapped from the canonical listing
// category (shared/listing-taxonomy.ts: produce, eggs, baked, prepared, meat,
// pantry, flowers, homemade, garden, other). Matches USDA's staple-food rule —
// produce, meat, dairy/eggs, breads/cereals, pantry staples, and food-producing
// seeds/plants are eligible; hot/prepared foods and non-food items are not.
// This is a starting default for demoing the checkout partition, not a
// substitute for USDA/FNS sign-off before any real EBT transaction runs.
export const SNAP_ELIGIBLE_CATEGORIES: ReadonlySet<string> = new Set([
  "produce",
  "eggs",
  "baked",
  "meat",
  "pantry",
]);

// "garden" needs subtype-level handling: its "plant" subtype (shared/
// listing-taxonomy.ts) covers both food-producing plants (a tomato start)
// and purely ornamental ones (a pothos, morning glories) with no way to
// tell them apart today. Only USDA's actual carve-out — seeds/plants that
// produce food — should read as eligible, so require the unambiguous
// seedling/starter subtypes rather than defaulting the whole category in.
const GARDEN_ELIGIBLE_SUBTYPES: ReadonlySet<string> = new Set(["seedling", "starter"]);

export function isSnapEligibleItem(category: string | null | undefined, subtype?: string | null): boolean {
  if (!category) return false;
  if (category === "garden") return !!subtype && GARDEN_ELIGIBLE_SUBTYPES.has(subtype);
  return SNAP_ELIGIBLE_CATEGORIES.has(category);
}

// Category-only convenience for call sites without a subtype handy — treats
// "garden" conservatively as ineligible rather than guessing.
export function isSnapEligibleCategory(category: string | null | undefined): boolean {
  return isSnapEligibleItem(category, null);
}

// Master switch for every piece of EBT/SNAP UI. Stoop is not FNS/USDA
// authorized yet and none of this may be visible to the public before it is —
// no badge, no browse filter, no split-tender line. The two flags below are the
// real authorization model, but either could be set in the database by mistake
// and expose the feature, so this gate sits in front of both.
//
// To enable EBT once authorization lands, set this to true. That is the only
// edit required: every surface routes through isEbtVisible().
export const SNAP_PUBLICLY_ENABLED = false;

// Whether a seller's items should show as EBT-visible: either Stoop itself
// holds platform-wide SNAP online authorization (covers every seller), or
// this specific seller has their own SNAP authorization on file.
export function isEbtVisible(sellerSnapApproved: boolean, platformSnapApproved: boolean): boolean {
  if (!SNAP_PUBLICLY_ENABLED) return false;
  return platformSnapApproved || sellerSnapApproved;
}
